import React, { useEffect, useMemo, useState } from "react";
import { usePutCustomAgentFrontmatterMutation } from "../../../../app/api/console/customAgentsApiSlice";
import {
  KNOWN_FIELD_DEFS,
  KNOWN_KEYS,
  LOGO_TYPE_OPTIONS,
  SEVERITY_OPTIONS,
  emptyValueForType,
  normalizeFrontmatter,
} from "./frontmatterSchema";

function stringifyExtra(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function parseExtra(raw) {
  const trimmed = raw.trim();
  if (trimmed === "") return "";
  if (trimmed === "true") return true;
  if (trimmed === "false") return false;
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  if (
    (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
    (trimmed.startsWith("{") && trimmed.endsWith("}"))
  ) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return raw;
    }
  }
  return raw;
}

function asStringList(value) {
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === "string" ? v : String(v ?? ""))).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
}

function asSubSkills(value) {
  if (!Array.isArray(value)) return [];
  return value.map((entry) => {
    if (typeof entry === "string") {
      return { name: entry, gates: [] };
    }
    if (entry && typeof entry === "object") {
      const gates = Array.isArray(entry.gates)
        ? entry.gates.map((g) => ({
            id: g?.id != null ? String(g.id) : "",
            name: g?.name != null ? String(g.name) : "",
          }))
        : [];
      return { name: entry.name != null ? String(entry.name) : "", gates };
    }
    return { name: "", gates: [] };
  });
}

function FieldShell({ label, hint, children, onRemove }) {
  return (
    <div className="border border-border-subtle rounded-xl p-3 space-y-2 bg-white">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-body-xs font-semibold text-text-primary">{label}</div>
          {hint && <p className="text-caption text-text-tertiary mt-0.5">{hint}</p>}
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-caption text-red-500 hover:text-red-700 shrink-0"
            title="Remove field"
          >
            Remove
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function StringListEditor({ value, onChange, suggestions = [] }) {
  const items = asStringList(value);
  const [draft, setDraft] = useState("");

  const addItem = (raw) => {
    const v = (raw || "").trim();
    if (!v) return;
    if (items.includes(v)) {
      setDraft("");
      return;
    }
    onChange([...items, v]);
    setDraft("");
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {items.length === 0 && (
          <span className="text-caption text-text-tertiary">No items</span>
        )}
        {items.map((item) => (
          <span
            key={item}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-surface-neutral text-body-xs text-text-primary"
          >
            {item}
            <button
              type="button"
              className="text-text-tertiary hover:text-red-600"
              onClick={() => onChange(items.filter((x) => x !== item))}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addItem(draft);
            }
          }}
          list={suggestions.length ? `suggestions-${suggestions.join("-")}` : undefined}
          placeholder="Add item…"
          className="flex-1 px-2.5 py-1.5 border border-border-subtle rounded-lg text-body-xs focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
        />
        {suggestions.length > 0 && (
          <datalist id={`suggestions-${suggestions.join("-")}`}>
            {suggestions.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        )}
        <button
          type="button"
          onClick={() => addItem(draft)}
          className="px-2.5 py-1.5 text-body-xs font-medium text-accent-purple border border-border-subtle rounded-lg hover:bg-surface-neutral"
        >
          Add
        </button>
      </div>
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {suggestions
            .filter((s) => !items.includes(s))
            .slice(0, 8)
            .map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => addItem(s)}
                className="px-2 py-0.5 rounded-md text-[10px] text-text-secondary bg-surface-neutral hover:bg-accent-purple/10"
              >
                + {s}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

function SubSkillsEditor({ value, onChange }) {
  const entries = asSubSkills(value);

  const update = (next) => onChange(next);

  return (
    <div className="space-y-3">
      {entries.map((entry, idx) => (
        <div
          key={idx}
          className="rounded-lg border border-border-subtle bg-surface-neutral/40 p-2.5 space-y-2"
        >
          <div className="flex gap-2 items-center">
            <input
              value={entry.name}
              onChange={(e) => {
                const next = [...entries];
                next[idx] = { ...entry, name: e.target.value };
                update(next);
              }}
              placeholder="subskill name"
              className="flex-1 px-2.5 py-1.5 border border-border-subtle rounded-lg text-body-xs font-mono bg-white focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
            />
            <button
              type="button"
              onClick={() => update(entries.filter((_, i) => i !== idx))}
              className="text-caption text-red-500 hover:text-red-700"
            >
              Remove
            </button>
          </div>
          <div className="space-y-1.5 pl-1">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
              Gates
            </div>
            {(entry.gates || []).map((gate, gIdx) => (
              <div key={gIdx} className="flex gap-1.5 items-start">
                <input
                  value={gate.id}
                  onChange={(e) => {
                    const next = [...entries];
                    const gates = [...(entry.gates || [])];
                    gates[gIdx] = { ...gate, id: e.target.value };
                    next[idx] = { ...entry, gates };
                    update(next);
                  }}
                  placeholder="gate-id"
                  className="w-40 shrink-0 px-2 py-1.5 border border-border-subtle rounded-lg text-[11px] font-mono bg-white focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
                />
                <input
                  value={gate.name}
                  onChange={(e) => {
                    const next = [...entries];
                    const gates = [...(entry.gates || [])];
                    gates[gIdx] = { ...gate, name: e.target.value };
                    next[idx] = { ...entry, gates };
                    update(next);
                  }}
                  placeholder="Gate description"
                  className="flex-1 px-2 py-1.5 border border-border-subtle rounded-lg text-[11px] bg-white focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
                />
                <button
                  type="button"
                  onClick={() => {
                    const next = [...entries];
                    next[idx] = {
                      ...entry,
                      gates: (entry.gates || []).filter((_, i) => i !== gIdx),
                    };
                    update(next);
                  }}
                  className="text-red-500 text-body-xs px-1"
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                const next = [...entries];
                next[idx] = {
                  ...entry,
                  gates: [...(entry.gates || []), { id: "", name: "" }],
                };
                update(next);
              }}
              className="text-[11px] font-medium text-accent-purple hover:underline"
            >
              + Add gate
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() => update([...entries, { name: "", gates: [{ id: "", name: "" }] }])}
        className="text-body-xs font-medium text-accent-purple hover:underline"
      >
        + Add sub-skill
      </button>
    </div>
  );
}

function IssueDefaultsEditor({ value, onChange }) {
  const obj =
    value && typeof value === "object" && !Array.isArray(value)
      ? value
      : { logo_type: "", severity: "" };
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <label className="block text-caption text-text-tertiary mb-1">logo_type</label>
        <select
          value={obj.logo_type || ""}
          onChange={(e) => onChange({ ...obj, logo_type: e.target.value })}
          className="w-full px-2.5 py-1.5 border border-border-subtle rounded-lg text-body-xs bg-white focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
        >
          <option value="">—</option>
          {LOGO_TYPE_OPTIONS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
          {obj.logo_type && !LOGO_TYPE_OPTIONS.includes(obj.logo_type) && (
            <option value={obj.logo_type}>{obj.logo_type}</option>
          )}
        </select>
      </div>
      <div>
        <label className="block text-caption text-text-tertiary mb-1">severity</label>
        <select
          value={obj.severity || ""}
          onChange={(e) => onChange({ ...obj, severity: e.target.value })}
          className="w-full px-2.5 py-1.5 border border-border-subtle rounded-lg text-body-xs bg-white focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
        >
          <option value="">—</option>
          {SEVERITY_OPTIONS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function ExploratoryEditor({ value, onChange }) {
  const obj =
    value && typeof value === "object" && !Array.isArray(value)
      ? value
      : { discovery: "", synthesis: "", issue_family: "" };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-caption text-text-tertiary mb-1">discovery</label>
          <input
            value={obj.discovery || ""}
            onChange={(e) => onChange({ ...obj, discovery: e.target.value })}
            className="w-full px-2.5 py-1.5 border border-border-subtle rounded-lg text-body-xs font-mono focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
            placeholder="discovery"
          />
        </div>
        <div>
          <label className="block text-caption text-text-tertiary mb-1">synthesis</label>
          <input
            value={obj.synthesis || ""}
            onChange={(e) => onChange({ ...obj, synthesis: e.target.value })}
            className="w-full px-2.5 py-1.5 border border-border-subtle rounded-lg text-body-xs font-mono focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
            placeholder="explorer"
          />
        </div>
      </div>
      <div>
        <label className="block text-caption text-text-tertiary mb-1">issue_family</label>
        <textarea
          value={obj.issue_family || ""}
          onChange={(e) => onChange({ ...obj, issue_family: e.target.value })}
          rows={3}
          className="w-full px-2.5 py-1.5 border border-border-subtle rounded-lg text-body-xs focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
        />
      </div>
    </div>
  );
}

function JourneysEditor({ value, onChange }) {
  const obj =
    value && typeof value === "object" && !Array.isArray(value)
      ? value
      : { required: false };
  return (
    <label className="flex items-center gap-2 text-body-xs text-text-secondary">
      <input
        type="checkbox"
        checked={Boolean(obj.required)}
        onChange={(e) => onChange({ ...obj, required: e.target.checked })}
        className="rounded border-border-default text-accent-purple focus:ring-accent-purple/30"
      />
      journeys.required — block clean exit until submit_test_result
    </label>
  );
}

function JsonEditor({ value, onChange }) {
  const [text, setText] = useState(() => stringifyExtra(value ?? {}));
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    setText(stringifyExtra(value ?? {}));
    setLocalError(null);
  }, [value]);

  return (
    <div className="space-y-1">
      <textarea
        value={text}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          try {
            const parsed = next.trim() ? JSON.parse(next) : {};
            setLocalError(null);
            onChange(parsed);
          } catch (err) {
            setLocalError(err.message);
          }
        }}
        rows={4}
        className="w-full px-2.5 py-1.5 border border-border-subtle rounded-lg text-[11px] font-mono focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
      />
      {localError && (
        <p className="text-caption text-amber-700">Invalid JSON: {localError}</p>
      )}
    </div>
  );
}

function renderTypedControl(def, value, onChange) {
  switch (def.type) {
    case "textarea":
      return (
        <textarea
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full px-2.5 py-1.5 border border-border-subtle rounded-lg text-body-xs focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
        />
      );
    case "select": {
      const options = def.options || [];
      const current = value ?? "";
      const needsCustom = current && !options.includes(current);
      return (
        <select
          value={current}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-2.5 py-1.5 border border-border-subtle rounded-lg text-body-xs bg-white focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
        >
          <option value="">—</option>
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
          {needsCustom && <option value={current}>{current}</option>}
        </select>
      );
    }
    case "string_list":
      return (
        <StringListEditor
          value={value}
          onChange={onChange}
          suggestions={def.suggestions || []}
        />
      );
    case "sub_skills":
      return <SubSkillsEditor value={value} onChange={onChange} />;
    case "issue_defaults":
      return <IssueDefaultsEditor value={value} onChange={onChange} />;
    case "exploratory":
      return <ExploratoryEditor value={value} onChange={onChange} />;
    case "journeys":
      return <JourneysEditor value={value} onChange={onChange} />;
    case "json":
      return <JsonEditor value={value} onChange={onChange} />;
    case "string":
    default:
      return (
        <input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-2.5 py-1.5 border border-border-subtle rounded-lg text-body-xs font-mono focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
        />
      );
  }
}

/**
 * Structured frontmatter editor for known SKILL.md fields, plus extras.
 */
function FrontmatterEditor({ env, slug, fileKey, frontmatter, onSaved }) {
  const [known, setKnown] = useState({});
  const [presentKeys, setPresentKeys] = useState([]);
  const [extraRows, setExtraRows] = useState([]);
  const [addKey, setAddKey] = useState("");
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [putFrontmatter, { isLoading }] = usePutCustomAgentFrontmatterMutation();

  useEffect(() => {
    const { known: k, extra } = normalizeFrontmatter(frontmatter);
    setKnown(k);
    // Preserve a stable order: schema order for known keys that exist, then any stray.
    const ordered = KNOWN_FIELD_DEFS.map((d) => d.key).filter((key) =>
      Object.prototype.hasOwnProperty.call(k, key)
    );
    for (const key of Object.keys(k)) {
      if (!ordered.includes(key)) ordered.push(key);
    }
    setPresentKeys(ordered);
    setExtraRows(
      Object.entries(extra).map(([key, value]) => ({
        key,
        value: stringifyExtra(value),
      }))
    );
    setDirty(false);
    setError(null);
    setAddKey("");
  }, [frontmatter, fileKey]);

  const missingDefs = useMemo(
    () => KNOWN_FIELD_DEFS.filter((d) => !presentKeys.includes(d.key)),
    [presentKeys]
  );

  const markDirty = () => setDirty(true);

  const setKnownValue = (key, value) => {
    setKnown((prev) => ({ ...prev, [key]: value }));
    markDirty();
  };

  const removeKnownKey = (key) => {
    setPresentKeys((prev) => prev.filter((k) => k !== key));
    setKnown((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    markDirty();
  };

  const addKnownKey = (key) => {
    if (!key || presentKeys.includes(key)) return;
    const def = KNOWN_FIELD_DEFS.find((d) => d.key === key);
    setPresentKeys((prev) => {
      const schemaOrder = KNOWN_FIELD_DEFS.map((d) => d.key);
      const next = [...prev, key];
      next.sort((a, b) => schemaOrder.indexOf(a) - schemaOrder.indexOf(b));
      return next;
    });
    setKnown((prev) => ({
      ...prev,
      [key]: emptyValueForType(def?.type || "string"),
    }));
    setAddKey("");
    markDirty();
  };

  const handleSave = async () => {
    setError(null);
    const out = {};
    for (const key of presentKeys) {
      if (!KNOWN_KEYS.has(key)) continue;
      let value = known[key];
      if (key === "sub_skills") {
        value = asSubSkills(value)
          .filter((e) => e.name.trim())
          .map((e) => ({
            name: e.name.trim(),
            gates: (e.gates || [])
              .filter((g) => g.id.trim() || g.name.trim())
              .map((g) => ({ id: g.id.trim(), name: g.name.trim() })),
          }));
      } else if (key === "applies_to" || key === "subskills" || key === "required_inputs") {
        value = asStringList(value);
      } else if (key === "zenyt_issue_defaults") {
        const obj = value && typeof value === "object" ? value : {};
        value = {};
        if (obj.logo_type) value.logo_type = obj.logo_type;
        if (obj.severity) value.severity = obj.severity;
      } else if (key === "exploratory") {
        const obj = value && typeof value === "object" ? value : {};
        value = {};
        if (obj.discovery) value.discovery = obj.discovery;
        if (obj.synthesis) value.synthesis = obj.synthesis;
        if (obj.issue_family) value.issue_family = obj.issue_family;
      } else if (key === "journeys") {
        const obj = value && typeof value === "object" ? value : {};
        value = { required: Boolean(obj.required) };
      }
      out[key] = value;
    }
    for (const row of extraRows) {
      const k = (row.key || "").trim();
      if (!k) continue;
      if (out[k] !== undefined || KNOWN_KEYS.has(k)) {
        setError(`Duplicate or reserved key: ${k}`);
        return;
      }
      out[k] = parseExtra(row.value);
    }
    try {
      await putFrontmatter({ env, slug, key: fileKey, frontmatter: out }).unwrap();
      setDirty(false);
      onSaved?.();
    } catch (err) {
      setError(err?.data?.detail || err?.error || "Save failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-body-xs text-text-secondary">
          Structured fields for SKILL.md frontmatter. Add only what this file needs.
        </p>
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || isLoading}
          className="px-3 py-1.5 bg-button-primary text-white rounded-lg text-body-xs font-semibold hover:bg-button-primary-hover disabled:opacity-40"
        >
          {isLoading ? "Saving…" : "Save frontmatter"}
        </button>
      </div>
      {error && (
        <div className="text-body-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>
      )}

      <div className="space-y-2.5">
        {presentKeys.map((key) => {
          const def = KNOWN_FIELD_DEFS.find((d) => d.key === key);
          if (!def) return null;
          return (
            <FieldShell
              key={key}
              label={def.label}
              hint={def.hint}
              onRemove={() => removeKnownKey(key)}
            >
              {renderTypedControl(def, known[key], (v) => setKnownValue(key, v))}
            </FieldShell>
          );
        })}
      </div>

      {missingDefs.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <select
            value={addKey}
            onChange={(e) => setAddKey(e.target.value)}
            className="px-2.5 py-1.5 border border-border-subtle rounded-lg text-body-xs bg-white focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
          >
            <option value="">Add known field…</option>
            {missingDefs.map((d) => (
              <option key={d.key} value={d.key}>
                {d.label} ({d.key})
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!addKey}
            onClick={() => addKnownKey(addKey)}
            className="px-2.5 py-1.5 text-body-xs font-medium text-accent-purple border border-border-subtle rounded-lg hover:bg-surface-neutral disabled:opacity-40"
          >
            Add field
          </button>
        </div>
      )}

      <div className="pt-2 border-t border-border-subtle space-y-2">
        <div className="text-body-xs font-semibold text-text-secondary">Extra fields</div>
        <p className="text-caption text-text-tertiary">
          Rare or custom keys not in the schema above (edited as text / JSON).
        </p>
        {extraRows.map((row, idx) => (
          <div key={idx} className="flex gap-2 items-start">
            <input
              value={row.key}
              onChange={(e) => {
                const next = [...extraRows];
                next[idx] = { ...row, key: e.target.value };
                setExtraRows(next);
                markDirty();
              }}
              className="w-40 shrink-0 px-2.5 py-2 border border-border-subtle rounded-lg text-body-xs font-mono focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
              placeholder="key"
            />
            <textarea
              value={row.value}
              onChange={(e) => {
                const next = [...extraRows];
                next[idx] = { ...row, value: e.target.value };
                setExtraRows(next);
                markDirty();
              }}
              rows={String(row.value).includes("\n") ? 3 : 1}
              className="flex-1 px-2.5 py-2 border border-border-subtle rounded-lg text-body-xs font-mono focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
              placeholder="value"
            />
            <button
              type="button"
              onClick={() => {
                setExtraRows(extraRows.filter((_, i) => i !== idx));
                markDirty();
              }}
              className="px-2 py-2 text-body-xs text-red-500 hover:text-red-700"
              title="Remove field"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => {
            setExtraRows([...extraRows, { key: "", value: "" }]);
            markDirty();
          }}
          className="text-body-xs font-medium text-text-secondary hover:text-accent-purple"
        >
          + Add custom field
        </button>
      </div>
    </div>
  );
}

export default FrontmatterEditor;
